import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useReducer,
} from 'react';

import { ExperimentAction } from '../../store/actions/experiment/action-creators';
import experimentReducer, {
  ExperimentState,
  initialExperimentState,
} from '../../store/reducers/experiment-reducer';

type ExperimentDispatch = Dispatch<ExperimentAction>;

const defaultDispatch: ExperimentDispatch = () => {
  throw new Error(
    'ExperimentDispatch was called outside of ExperimentProvider',
  );
};

export const ExperimentContext = createContext<ExperimentState>(
  initialExperimentState,
);
export const ExperimentDispatchContext =
  createContext<ExperimentDispatch>(defaultDispatch);

export const useExperimentContext = () => {
  const context = useContext(ExperimentContext);
  if (context === undefined) {
    throw new Error(
      'useExperimentContext must be used within a ExperimentProvider. Make sure the component is wrapped in ExperimentProvider.',
    );
  }
  return context;
};

export const useExperimentDispatchContext = () => {
  const dispatch = useContext(ExperimentDispatchContext);
  if (dispatch === defaultDispatch) {
    throw new Error(
      'useExperimentDispatchContext must be used within a ExperimentProvider. Make sure the component is wrapped in ExperimentProvider.',
    );
  }
  return dispatch;
};

export const ExperimentProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    experimentReducer,
    initialExperimentState,
  );

  return (
    <ExperimentContext.Provider value={state}>
      <ExperimentDispatchContext.Provider value={dispatch}>
        {children}
      </ExperimentDispatchContext.Provider>
    </ExperimentContext.Provider>
  );
};

export default ExperimentProvider;
